import { globalVariables } from "helper/globalVariables";
import Dropdown from "shared/Components/dropdown";
import { SearchLocation } from "shared/Components/searchLocation";
import SearchBar from "shared/Components/search";
import { useEffect, useState, useRef } from "react";
import { debounce } from "lodash";
import { PopUpMessages } from "shared/Components/popUpMessages";
import { TableWithPagination } from "shared/Components/tableWithPagination";
import { globalClasses } from "helper/globalClasses";
import { JudgeListMobileTable } from "./JudgeListMobileTable";
import { useLazyQuery } from "@apollo/client";
import { client } from "helper/apolloConfig";
import {
  getJudgeListQuery,
  updateJudgeLevel,
  DOWNLOAD_JUDGES_REPORT,
} from "redux/features/hostSlice/judgeSlice/judgeGql";
import useEffectAfterMount from "helper/useEffectAfterMount";
import { MultiSelectChipsWithSearchDropdown } from "shared/Components/multiSelectWithSearchAndChips";
import { useMediaQuery } from "react-responsive";
import PageInfo from "shared/Components/pageInfo";
import { DownloadWhiteAlt } from "helper/icons";
import { Button } from "shared/Components/button";
import { useQuery } from "@apollo/client";
import { handleDownload } from "helper/utils";

export const APPROVE_JUDGE = "Approved Judges";

export const APPROVE_OR_REJECT_JUDGE = "Approve/Reject Judges";

export const VIEW_JUDGE_TABS = [APPROVE_JUDGE, APPROVE_OR_REJECT_JUDGE];

const noviceDropdownData = [
  globalVariables.sit,
  globalVariables.novice,
  globalVariables.advanced,
  globalVariables.excellent,
  globalVariables.premier,
  globalVariables.provisional,
];

const headData = [
  globalVariables.judgeName,
  globalVariables.phoneNumber,
  globalVariables.email,
  globalVariables.city,
  globalVariables.state,
  globalVariables.zip,
  globalVariables.level,
  globalVariables.distanceReadyToTravel,
  globalVariables.numberOfRailJudge,
];

export const ApprovedJudges = ({ isAdmin  }) => {
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const [levelFilter, setLevelFilter] = useState([]);
  const [stateFilter, setStateFilter] = useState(null);
  const [cityFilter, setCityFilter] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const { tableDataCls, tableDataSmallCls, paginatedDataCls } = globalClasses;
  const [getData, { loading, error, data, refetch }] = useLazyQuery(getJudgeListQuery, {
    fetchPolicy: "network-only",
  });

  const popUpModalRef = useRef();

  const [apiData, setApiData] = useState({
    title: "",
    desc: "",
    type: "pending",
  });

  const options = noviceDropdownData.map((data, index) => ({
    label: data,
    value: `option_${index}`,
  }));

  const handleFilterChange = (selectedOptions) => {
    const selectedValues = selectedOptions.map((option) => option.label);
    setLevelFilter(selectedValues);
  };

  const levels = ["Novice", "Advanced", "Excellent", "Premier", "Provisional"];

  const onUpdateLevel = async (id, level) => {
    try {
      const { data } = await client.mutate({
        mutation: updateJudgeLevel,
        variables: {
          authId: id,
          level: [level],
        },
      });
      setApiData({
        title: "Success",
        desc: data.updateJudgeLevel.message,
        type: "success",
      });
      refetch();
      popUpModalRef.current.openModal();
    } catch (error) {
      setApiData({
        title: "error",
        desc: error.message,
        type: "pending",
      });
    }
  }
  
  const resultData = data?.getJudgeList;
  const judges = resultData?.data || [];

  useEffect(() => {
    setTotalJudges(judges.length)
  }, [judges]);

  const handlePaginationData = (page = 1) => {
    setPage(page);
  };

  useEffect(() => {
    getData({
      variables: {
        pageNumber: page,
        pageSize: 10,
        level: levelFilter.length === 0 ? null : levelFilter,
        city: cityFilter,
        state: stateFilter,
        search: search,
        judgeStatus: "approved",
      },
    });
  }, [page]);

  useEffectAfterMount(() => {
    setPage(1);
    getData({
      variables: {
        pageNumber: 1,
        pageSize: 10,
        level: levelFilter.length === 0 ? null : levelFilter,
        city: cityFilter,
        state: stateFilter,
        search: search,
        judgeStatus: "approved",
      },
    });
  }, [levelFilter, cityFilter, stateFilter, search]);

  const renderTableBody = () => {
    return judges.map((judge, index) => (
      <tr key={judge.name}>
        <td className={paginatedDataCls(index)}>{judge.name ?? "-"}</td>
        <td className={paginatedDataCls(index)}>{judge.phone_number ?? "-"}</td>
        <td className={paginatedDataCls(index)}>{judge.email ?? "-"}</td>
        <td className={paginatedDataCls(index)}>{judge.city ?? "-"}</td>
        <td className={paginatedDataCls(index)}>{judge.state ?? "-"}</td>
        <td className={paginatedDataCls(index)}>{judge.zipCode ?? "-"}</td>
        <td className={paginatedDataCls(index)}>
          {isAdmin ? (
            <Dropdown
              defaultOption={judge.level[0]}
              width="w-[100px]"
              dropdownOptions={levels.filter(e => e !== judge.level[0])}
              onChange={(value) => onUpdateLevel(judge.authId, value)}
          />
          ) : (
            judge.level[0]
          )}
        </td>
        <td className={paginatedDataCls(index)}>
          {judge.distance_travel ?? "-"}
        </td>
        <td className={paginatedDataCls(index)}>
          {judge.noofTrialJudged ?? "-"}
        </td>
      </tr>
    ));
  };

  const onSearch = (event) => {
    const value = event?.target?.value;
    setSearch(value);
  };

  const debouncedSearch = debounce(onSearch, 1000);

  const [totalJudegs, setTotalJudges] = useState(0)

  const {
    loading: downloadLoading,
    error: ErrorLoading,
    data: downloadData,
  } = useQuery(DOWNLOAD_JUDGES_REPORT, {
    fetchPolicy: "network-only",
  });

  const onClickHandler = async () => {
    const downloadUrl = downloadData?.generateAllJudgesReport?.downloadUrl;
    await handleDownload(downloadUrl);
  } 

  const onClearFilters = () => {
    setCityFilter(null);
    setStateFilter(null);
    setLevelFilter([]);
    setSearch("");
  };

  return (
    <> 
      <PageInfo  
        title={globalVariables.viewJudges}
      >
      <div className="flex gap-2">
        <Button
          width={"w-auto"}
          prefix={false}
          label={globalVariables.download}
          icon={DownloadWhiteAlt}
          disabled={downloadLoading && !totalJudegs} 
          onClick={onClickHandler}
        />
        <Button
          width={"w-auto"}
          label="Clear All Filters"
          onClick={onClearFilters} 
        />
      </div>
      </PageInfo> 
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 my-5 order-3">
        <SearchBar
          defaultValue={search}
          isShadow={true}
          width={"w-full"}
          onChange={debouncedSearch}
          placeholder={"Name"}
          isFaded={true}
        />
          <SearchLocation
            isShadow={true}
            width={"w-full"}
            name="state"
            placeholder={globalVariables.state}
            types={["(states)"]}
            onSelectLocation={(location) => {
              setStateFilter(location);
            }}
            defaultValue={stateFilter}
          />
          <SearchLocation
            isShadow={true}
            width={"w-full"}
            name="city"
            placeholder={globalVariables.city}
            types={["(cities)"]}
            onSelectLocation={(location) => {
              setCityFilter(location);
            }}
            searchBy={stateFilter}
            defaultValue={cityFilter}
          />
          <MultiSelectChipsWithSearchDropdown
            width={"w-full"}
            placeholder={globalVariables.level}
            options={options}
            label=""
            onChange={handleFilterChange}
            defaultSelectedOptions={levelFilter}
          />
        </div>
        <div className="order-4">
          {isMobile ? (
            <JudgeListMobileTable
              judges={judges}
              currentPage={page}
              totalPages={resultData?.totalPages}
              loading={loading}
              handlePageChange={(page) => handlePaginationData(page)}
            />
          ) : (
            <TableWithPagination
              headData={headData}
              tableBody={renderTableBody()}
              itemsPerPage={10}
              tableHeight={"md:h-[calc(100vh-10rem)] lg:h-[100vh]"}
              totalPage={resultData?.totalPages}
              handlePageChange={(page) => handlePaginationData(page)}
              loading={loading}
              current={page}
              persistPagination={true}
              error={error}
          />
          )}
        </div>
      <PopUpMessages
        title={apiData.title}
        desc={apiData.desc}
        type={apiData.type}
        ref={popUpModalRef}
      />
    </>
  );
};
